// Modal explaining why an account is throttled, suspended, or terminated.

import { Modal } from '@/components/ui/modal'
import type { Account } from '@/lib/auth-context'
import { humanizeReason } from '@/lib/suspension-utils'

interface SuspensionModalProps {
  open: boolean
  onClose: () => void
  account: Account
}

export function SuspensionModal({ open, onClose, account }: SuspensionModalProps): React.JSX.Element {
  const level = account.suspension_level ?? 0
  const summary = humanizeReason(account.pause_reason)

  if (level === 1) return <ThrottledModal open={open} onClose={onClose} summary={summary} throttle={account.throttle_percent ?? 50} />
  if (level === 2) return <SuspendedModal open={open} onClose={onClose} summary={summary} expiresAt={account.suspension_expires_at} />
  if (level === 3) return <UnderReviewModal open={open} onClose={onClose} summary={summary} appealStatus={account.appeal_status} />
  if (level === 4) return <TerminatedModal open={open} onClose={onClose} summary={summary} />

  return <ThrottledModal open={open} onClose={onClose} summary={summary} throttle={50} />
}

function SectionHeading({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <h3 className="mb-1 text-sm font-semibold text-text-primary">{children}</h3>
}

function SectionText({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <p className="text-sm leading-relaxed text-text-secondary">{children}</p>
}

function SupportLine(): React.JSX.Element {
  return (
    <p className="text-sm text-text-secondary">
      Need help? Contact{' '}
      <a href="mailto:support@one-email.com" className="font-medium text-brand-500 hover:text-brand-600">
        support@one-email.com
      </a>
    </p>
  )
}

function ModalActions({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="mt-6 flex items-center gap-3">{children}</div>
}

function PrimaryButton({ href, children }: { href: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <a href={href} className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600">
      {children}
    </a>
  )
}

function CloseButton({ onClick }: { onClick: () => void }): React.JSX.Element {
  return (
    <button type="button" onClick={onClick} className="rounded-lg border border-surface-3 px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-2">
      Close
    </button>
  )
}

// --- Level 1: Throttled ---

function ThrottledModal({ open, onClose, summary, throttle }: {
  open: boolean
  onClose: () => void
  summary: string
  throttle: number
}): React.JSX.Element {
  return (
    <Modal open={open} onClose={onClose} title="Sending Throttled">
      <div className="space-y-4">
        <SectionText>Your sending speed has been reduced to {throttle}%.</SectionText>

        <div>
          <SectionHeading>What happened</SectionHeading>
          <SectionText>
            {summary} Complaints are when recipients mark your email as spam.
          </SectionText>
        </div>

        <div>
          <SectionHeading>What we did</SectionHeading>
          <SectionText>
            Throttled sending to protect your sender reputation and deliverability.
          </SectionText>
        </div>

        <div>
          <SectionHeading>Next steps</SectionHeading>
          <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-text-secondary">
            <li>Review recent sends for unwanted content</li>
            <li>Ensure your suppression list is up to date</li>
            <li>Check your current rates under Settings &gt; Account Health</li>
            <li>If your rates drop below threshold, sending resumes automatically within 24 hours</li>
          </ul>
        </div>

        <SupportLine />

        <ModalActions>
          <PrimaryButton href="/settings">Go to Settings</PrimaryButton>
          <CloseButton onClick={onClose} />
        </ModalActions>
      </div>
    </Modal>
  )
}

// --- Level 2: Suspended (timed) ---

function SuspendedModal({ open, onClose, summary, expiresAt }: {
  open: boolean
  onClose: () => void
  summary: string
  expiresAt?: string | null
}): React.JSX.Element {
  const expiresText = expiresAt
    ? new Date(expiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
    : 'when conditions improve'

  return (
    <Modal open={open} onClose={onClose} title="Sending Paused">
      <div className="space-y-4">
        <SectionText>Your sending has been temporarily paused.</SectionText>

        <div>
          <SectionHeading>What happened</SectionHeading>
          <SectionText>{summary}</SectionText>
        </div>

        <div>
          <SectionHeading>What we did</SectionHeading>
          <SectionText>
            Paused all sending to protect your sender reputation and our shared infrastructure.
          </SectionText>
        </div>

        <div>
          <SectionHeading>When does it resume?</SectionHeading>
          <SectionText>
            Sending resumes automatically on {expiresText}. If this keeps happening, future pauses will be longer.
          </SectionText>
        </div>

        <SupportLine />

        <ModalActions>
          <PrimaryButton href="/settings?tab=appeal">Submit Appeal</PrimaryButton>
          <CloseButton onClick={onClose} />
        </ModalActions>
      </div>
    </Modal>
  )
}

// --- Level 3: Under Review ---

function UnderReviewModal({ open, onClose, summary, appealStatus }: {
  open: boolean
  onClose: () => void
  summary: string
  appealStatus?: string | null
}): React.JSX.Element {
  const hasPendingAppeal = appealStatus === 'pending'

  return (
    <Modal open={open} onClose={onClose} title="Sending Paused — Under Review">
      <div className="space-y-4">
        <SectionText>Your account is being reviewed by our team.</SectionText>

        <div>
          <SectionHeading>What happened</SectionHeading>
          <SectionText>{summary}</SectionText>
        </div>

        <div>
          <SectionHeading>What does this mean?</SectionHeading>
          <SectionText>
            All sending is paused until our team completes their review. This typically takes 1–2 business days.
          </SectionText>
        </div>

        {hasPendingAppeal ? (
          <div className="rounded-lg border border-warning/30 bg-warning/10 px-4 py-3">
            <p className="text-sm font-medium text-warning-700">
              Your appeal is being reviewed. We'll notify you by email when a decision is made.
            </p>
          </div>
        ) : (
          <SectionText>You can submit an appeal to speed up the process.</SectionText>
        )}

        <SupportLine />

        <ModalActions>
          {hasPendingAppeal ? (
            <span className="rounded-lg bg-warning/20 px-4 py-2 text-sm font-medium text-warning-700">
              Appeal Pending
            </span>
          ) : (
            <PrimaryButton href="/settings?tab=appeal">Submit Appeal</PrimaryButton>
          )}
          <CloseButton onClick={onClose} />
        </ModalActions>
      </div>
    </Modal>
  )
}

// --- Level 4: Terminated ---

function TerminatedModal({ open, onClose, summary }: {
  open: boolean
  onClose: () => void
  summary: string
}): React.JSX.Element {
  return (
    <Modal open={open} onClose={onClose} title="Account Terminated">
      <div className="space-y-4">
        <SectionText>Your account has been permanently suspended.</SectionText>

        <div>
          <SectionHeading>What happened</SectionHeading>
          <SectionText>{summary}</SectionText>
        </div>

        <div>
          <SectionHeading>What does this mean?</SectionHeading>
          <SectionText>
            All sending has been stopped and cannot be resumed automatically.
            API keys remain active for reading data but cannot send emails.
          </SectionText>
        </div>

        <SectionText>
          If you believe this is an error, contact us at{' '}
          <a href="mailto:support@one-email.com" className="font-medium text-brand-500 hover:text-brand-600">
            support@one-email.com
          </a>{' '}
          and include your account ID.
        </SectionText>

        <ModalActions>
          <a
            href="mailto:support@one-email.com"
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
          >
            Email Support
          </a>
          <CloseButton onClick={onClose} />
        </ModalActions>
      </div>
    </Modal>
  )
}
